import { z } from 'zod';
/**
 * A conversa interna do MyMAXX: a equipe entre si e com o técnico de campo.
 *
 * Nasceu para acabar com o Telegram (decisão de 06/10). A empresa tinha a tratativa sobre
 * um cliente num privado fora do seu controle, sem registro e sem como achar depois — e
 * quem atende precisava de dois aplicativos abertos o dia todo.
 *
 * **Não é atendimento.** Não tem protocolo, fila, SLA, motivo de encerramento nem
 * avaliação, e não entra em relatório de atendimento. O que é igual são os meios: mídia,
 * áudio, citação, aviso e som são os mesmos do chat com o cliente.
 */
export const TIPOS_CANAL = ['direto', 'grupo'];
/** Dono e admin são quem pode gerir o grupo; membro só participa. */
export function podeGerirGrupo(papel) {
    return papel === 'dono' || papel === 'admin';
}
export const secaoFormSchema = z.object({
    nome: z.string().trim().min(1, 'Dê um nome à seção').max(40),
});
/** Onde guardar uma conversa. `null` tira da seção e devolve à lista solta. */
export const guardarEmSecaoSchema = z.object({ secaoId: z.uuid().nullable() });
/**
 * Por quanto tempo silenciar.
 *
 * `null` é "até eu desligar": o silêncio sem prazo é o caso comum do grupo que apita o
 * dia todo. Quando há prazo, ele é em horas — ninguém pensa em "silenciar até 14:35".
 */
export const silenciarSchema = z.object({ horas: z.number().int().positive().max(720).nullable() });
export const fixarSchema = z.object({ fixado: z.boolean() });
export const favoritarCanalSchema = z.object({ favorito: z.boolean() });
export const arquivarSchema = z.object({ arquivado: z.boolean() });
/**
 * Abrir um canal direto com alguém.
 *
 * Um dos dois campos, nunca os dois: `usuarioId` para falar com a equipe, `clienteId` para
 * falar com um técnico. Abrir duas vezes com a mesma pessoa cai sempre no mesmo canal.
 */
export const canalDiretoSchema = z
    .object({
    usuarioId: z.uuid().optional(),
    clienteId: z.uuid().optional(),
})
    .refine((v) => !!v.usuarioId !== !!v.clienteId, 'Escolha uma pessoa');
export const grupoFormSchema = z.object({
    nome: z.string().trim().min(2, 'Dê um nome ao grupo').max(80),
    descricao: z.string().trim().max(300).optional().or(z.literal('')),
    /** Quem entra junto. Quem cria entra sempre, como dono. */
    usuarioIds: z.array(z.uuid()).max(200).default([]),
    /** Técnicos de campo no grupo: eles veem tudo o que for dito ali. */
    clienteIds: z.array(z.uuid()).max(200).default([]),
});
/** As palavras que o dono do grupo não quer ver ali dentro. */
export const palavrasSensiveisSchema = z.object({
    palavras: z.array(z.string().trim().min(2).max(60)).max(100),
});
/**
 * Quanto tempo uma mensagem fica fixada.
 *
 * Prazo, e não só "fixar": o recado de hoje não deveria continuar no alto da conversa em
 * março. Quem quer que continue escolhe "sem prazo" e assume isso.
 */
export const PRAZOS_DE_FIXACAO = [
    { id: '24h', rotulo: '24 horas', horas: 24 },
    { id: '7d', rotulo: '7 dias', horas: 24 * 7 },
    { id: '30d', rotulo: '30 dias', horas: 24 * 30 },
    { id: 'sempre', rotulo: 'Sem prazo', horas: null },
];
export const fixarMensagemSchema = z.object({
    prazo: z.enum(['24h', '7d', '30d', 'sempre']),
});
/**
 * Quantas mensagens cabem fixadas ao mesmo tempo numa conversa.
 *
 * Cinco cobre "o combinado", "o link da planilha" e "o aviso da semana" sem a barra do alto
 * virar um mural que ninguém lê.
 */
export const LIMITE_FIXADAS = 5;
/** Promover a admin, ou rebaixar a membro. Só o dono do grupo faz isso. */
export const papelDoMembroSchema = z.object({
    usuarioId: z.uuid().optional(),
    clienteId: z.uuid().optional(),
    papel: z.enum(['admin', 'membro']),
});
/** Nome e descrição do grupo. Dono e admin podem mudar. */
export const editarGrupoSchema = z.object({
    nome: z.string().trim().min(2).max(80),
    descricao: z.string().trim().max(240).nullable(),
});
/** Para onde encaminhar uma mensagem. */
export const encaminharSchema = z.object({ paraCanalId: z.uuid() });
export const mensagemCanalSchema = z.object({
    texto: z.string().trim().min(1, 'Escreva uma mensagem').max(4000),
    respondeAId: z.uuid().optional(),
});
/** Corrigir o que já foi dito. O fio continua mostrando que a mensagem foi editada. */
export const editarMensagemCanalSchema = z.object({
    texto: z.string().trim().min(1, 'Escreva uma mensagem').max(4000),
});
export const membrosCanalSchema = z.object({
    usuarioIds: z.array(z.uuid()).max(200).default([]),
    clienteIds: z.array(z.uuid()).max(200).default([]),
});
/** Namespace do WebSocket da conversa interna. */
export const NS_INTERNO = '/interno';
/**
 * O que o servidor empurra no canal interno.
 *
 * Separado dos eventos do atendimento porque o público é outro: aqui o mesmo socket serve
 * a equipe e ao técnico, e a entrega é pela sala pessoal de cada membro — não há sala de
 * conversa que dê conta de gente entrando e saindo de grupo.
 */
export const EVENTO_INTERNO = {
    /** Mensagem nova num canal de que você participa. */
    MENSAGEM: 'interno_mensagem',
    /**
     * Uma mensagem que já estava no fio mudou: foi editada ou apagada.
     *
     * Evento próprio, e não `MENSAGEM` de novo: a tela toca som e acende contador quando
     * chega mensagem, e uma correção de digitação não é mensagem nova.
     */
    MENSAGEM_MUDOU: 'interno_mensagem_mudou',
    /** O canal mudou: nome, membros, alguém saiu. */
    CANAL: 'interno_canal',
    /** Alguém leu o canal até certo ponto — é o que pinta o "visto" das bolhas. */
    LIDO: 'interno_lido',
    /** Alguém está escrevendo agora. Vai e vem; nada é guardado. */
    DIGITANDO: 'interno_digitando',
    /** Alguém abriu ou fechou o MyMAXX. */
    PRESENCA: 'interno_presenca',
    // ── Chamada de voz ──────────────────────────────────────────────────────────
    //
    // O servidor só apresenta os dois lados: o áudio vai direto de um navegador ao outro.
    // Estes eventos carregam o "alô, está me ouvindo?" da conexão — descrições de mídia
    // (SDP) e caminhos de rede (ICE) —, nunca som.
    /** Está tocando aí: alguém abriu uma chamada para você. */
    CHAMANDO: 'interno_chamando',
    /** A descrição de mídia de um lado (SDP), de ida e de volta. */
    CHAMADA_SDP: 'interno_chamada_sdp',
    /** Um caminho de rede possível até mim (ICE). */
    CHAMADA_ROTA: 'interno_chamada_rota',
    /** Atendi. */
    CHAMADA_ATENDIDA: 'interno_chamada_atendida',
    /** Acabou — por desligar, recusar, não atender ou falhar. */
    CHAMADA_FIM: 'interno_chamada_fim',
};
/**
 * "Fulano está escrevendo…", com mais de um.
 *
 * Mora no pacote compartilhado porque o painel e o app do técnico dizem a mesma coisa, e
 * porque a regra do plural em português não cabe numa interpolação solta: "está" vira
 * "estão", e a partir de três nomes a frase fica mais longa que a conversa.
 */
export function fraseDeDigitando(nomes) {
    const primeiros = nomes.map((n) => n.split(' ')[0] ?? n);
    if (primeiros.length === 0)
        return null;
    if (primeiros.length === 1)
        return `${primeiros[0]} está escrevendo…`;
    if (primeiros.length === 2)
        return `${primeiros[0]} e ${primeiros[1]} estão escrevendo…`;
    return `${primeiros[0]} e mais ${primeiros.length - 1} estão escrevendo…`;
}
/** O aparelho confirmando que as mensagens chegaram. */
export const entregaSchema = z.object({
    mensagemIds: z.array(z.string().uuid()).min(1).max(200),
});
/** Ligar ou desligar o visto. */
export const confirmacaoDeLeituraSchema = z.object({ confirma: z.boolean() });
//# sourceMappingURL=canais.js.map